import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { AbstractControl, Validators } from '@angular/forms';
import { RequiredDocsUploadComponent } from '../required-docs-upload/required-docs-upload/required-docs-upload.component';
import { FileDisplayComponent } from '../required-docs-upload/file-display/file-display.component';
import { FILE_UPLOAD_STATUS } from '../required-docs-upload/interfaces/displayable-file.interface';
import { RequiredDocsUploadService } from '../required-docs-upload/services/required-docs-upload.service';
import { 
  MedigapRequiredDocumentUpload,
  MedigapRequiredDocumentUploadStateStructure,
  MEDIGAP_FORM_DOCUMENT_UPLOAD_REDUCER_NAME,
  saveDocUploadId,
  removeDocUploadId 
} from './medigap-form.doc-upload.reducer';

interface PageUploadDocumentFormControls {
  [nameFromFormConfig: string]: AbstractControl
}

@FormComponentLookup('form-required-docs-upload')
@Component({
    selector: 'app-form-required-docs-upload',
    templateUrl: './required-docs-upload-form.component.html',
    styleUrls: ['./required-docs-upload-form.component.scss'],
    standalone: false
})
export class RequiredDocsUploadFormComponent extends FormEntryComponentClass implements OnInit, AfterViewInit {

  @ViewChild('RequiredDocsUploadComponent') requiredDocsUploadComponent: RequiredDocsUploadComponent;
  
  /** Snatched from state and passed to children */
  existingDocs: MedigapRequiredDocumentUpload[];

  protected ngRedux: NgRedux<StateStructure>;
  protected requiredDocsUploadService: RequiredDocsUploadService;

  constructor() {
    super();
    this.ngRedux = this.injector.get(NgRedux);
    this.requiredDocsUploadService = this.injector.get(RequiredDocsUploadService);
  }

  ngOnInit(): void {
    const medigapFormDocumentUploadState: MedigapRequiredDocumentUploadStateStructure
      = this.reducerUtilityService.getComponentStates()[MEDIGAP_FORM_DOCUMENT_UPLOAD_REDUCER_NAME];

    if (medigapFormDocumentUploadState.documents.length > 0) {
      this.existingDocs = medigapFormDocumentUploadState.documents;
    }
  }

  ngAfterViewInit(): void {
    this.subscribeToFormStream(this.entry.base.formListeners);
    
    /** Wait until ngSwitch stuff before we try to validate */
    this.validateRequiredFormsUploaded();

    /** Don't let the user leave(!) until upload is successful/failed; @TODO update our PTLXHR to use observables instead of manually casting to promises */
    this.subscriptionGroup.push(this.requiredDocsUploadService.uploadPendingEmitter.subscribe(v => {
      const control = this.componentFormControls[this.entry.formControlAttributes.formControlName];
      if (v) {
        control.setErrors({ itemsPending: true });
      }
    }));
  }
  
  get componentFormControls(): PageUploadDocumentFormControls {
    return this.parentForm[this.entry.formControlAttributes.connectPath].controls as unknown as PageUploadDocumentFormControls;
  }

  protected validateRequiredFormsUploaded(): boolean {
    /**
     * Number of succesfully uploaded documents must match at least the minimum required amount
     */
    const successfullyUploadedDocuments = {
      minimumRequired: this.entry.custom?.minDocuments,
      actualAmountUploaded: this.requiredDocsUploadComponent?.fileDisplayArray ? this.requiredDocsUploadComponent?.fileDisplayArray.filter(f => f.status === FILE_UPLOAD_STATUS.SUCCESS).length : 0
    }

    const filesCurrentlyPresent = this.requiredDocsUploadComponent?.fileDisplayArray ? this.requiredDocsUploadComponent?.fileDisplayArray.length : 0;

    const control = this.componentFormControls[this.entry.formControlAttributes.formControlName];
    
    // Force the user to upload or remove files lingering
    control.setValidators(Validators.required);
    control.updateValueAndValidity();
    control.reset();

    if (filesCurrentlyPresent > successfullyUploadedDocuments.actualAmountUploaded) {
      control.setValue(null);
      control.updateValueAndValidity();
      return false;
    } else if (successfullyUploadedDocuments?.minimumRequired > 0 && successfullyUploadedDocuments.actualAmountUploaded >= successfullyUploadedDocuments.minimumRequired) {
      control.setValue('populated');
      control.updateValueAndValidity();
      return true;
    } else if (!successfullyUploadedDocuments.minimumRequired) {
      control.setValue('populated');
      control.updateValueAndValidity();
      return true;
    } else {
      control.setValue(null);
      control.updateValueAndValidity();
      return false;
    }

  }

  /**
   * Call `this` function from the child;
   * wait for child to finish rendering;
   * validate
   */
  protected triggerValidation(): () => void {
    return () => {
      this.validateRequiredFormsUploaded();
    }
  }

  saveResponseToState(fileComponent: FileDisplayComponent) {
    this.ngRedux.dispatch(saveDocUploadId(fileComponent.displayableFileObj));
  }

  removeResponseFromState(fileComponent: FileDisplayComponent) {
    this.ngRedux.dispatch(removeDocUploadId(fileComponent.displayableFileObj.documentRecord));
  }

  uploadFile() {
    return (fileComponent: FileDisplayComponent) => {
      this.saveResponseToState(fileComponent);
      this.validateRequiredFormsUploaded();
    }
  }

  removeFile() {
    return (fileComponent: FileDisplayComponent) => {    
      this.removeResponseFromState(fileComponent);
      this.validateRequiredFormsUploaded();
    }
  }

}